/**
 * Which app this is (docs/18-vorratsbuch.md, V0-1): KellerLog and VorratsBuch are one build, the
 * host name decides. The server writes name and colour into the page; this sets them again for the
 * development server, which delivers the page unchanged.
 */
import { appForHost, profileById } from "@lagerlog/core";

export const APP = appForHost(location.hostname);

export function applyBrand() {
  document.title = APP.name;
  // The stylesheet calls the app colour --wine (KellerLog came first).
  document.documentElement.style.setProperty("--wine", APP.theme);
  document.documentElement.style.setProperty("--wine-dark", APP.dark);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", APP.theme);
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", APP.short);
  document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute("href", `${APP.icons}/apple-touch-icon.png`);
}

/** The profiles of this app; the first gives the words of the app („Wein“, „Lebensmittel“). */
export const APP_PROFILES = APP.profiles.map(profileById);
export const MAIN_PROFILE = APP_PROFILES[0]!;
export const IS_PANTRY = APP.id === "vorratsbuch";

/** The storages to start with, each with the profile it is for (KellerLog: those of the wine). */
export const STARTERS = APP_PROFILES.flatMap((p) => p.starter.map((s) => ({ ...s, profile: p.id }))).filter((s, i, all) => all.findIndex((o) => o.id === s.id) === i);

/**
 * One order of storages everywhere (B5): as they are set up (the starters of the app's profiles,
 * Vorratsschrank, Kühlschrank, Gefriertruhe, Haushaltsschrank; Weinkeller, Bar), others after them
 * by name. `name` gives a storage's name by its id.
 */
export function compareLagers(name: (id: string) => string): (a: string, b: string) => number {
  const rank = (id: string) => {
    const i = STARTERS.findIndex((s) => s.id === id);
    return i < 0 ? STARTERS.length : i;
  };
  return (a, b) => rank(a) - rank(b) || name(a).localeCompare(name(b), "de", { numeric: true });
}

/**
 * Whether a storage belongs to this app (docs/18-vorratsbuch.md, V0-5): one household, two apps,
 * each with its storages. A storage without profile is from before and belongs to KellerLog.
 */
export function ownsLager(profile: unknown): boolean {
  const pantry = profile === "lebensmittel" || profile === "haushalt";
  return IS_PANTRY ? pantry : !pantry;
}

/** Whether an article belongs to this app, by its profile (articles without one are drinks). */
export const ownsArticle = (fields: Record<string, unknown>) => ownsLager(fields.profile);

/**
 * Taking out in words (B1): in VorratsBuch the profile's („Verbrauchen“, „Verbraucht“ in a
 * household), in KellerLog „Entnehmen“ and „Entnommen“ wherever the button does not say otherwise.
 */
export function takeWords(kind: string | undefined): { take: string; taken: string } {
  if (!IS_PANTRY) return { take: "Entnehmen", taken: "Entnommen" };
  const k = kind === "private" ? "private" : "business";
  return { take: MAIN_PROFILE.vocabulary.take[k], taken: MAIN_PROFILE.vocabulary.taken?.[k] ?? "Entnommen" };
}

/**
 * The words of the shared views (booking, stock, protocol, places): KellerLog speaks of wine and
 * bottles, VorratsBuch of articles and packs. A pantry view of its own uses its profile's words.
 */
export const WORDS = IS_PANTRY
  ? {
      one: "Artikel",
      many: "Artikel",
      aOne: "etwas",
      search: "Vorrat suchen",
      unit: "Packung",
      units: "Packungen",
      unitShort: "Pck.",
      lagerExample: "Vorratsschrank oder Kühlschrank",
      /** The location a new storage starts with (A2). */
      firstLocation: "Fach",
      lagers: "Vorratsschrank, Kühlschrank, Regale",
      device: "Ein Gerät in der Küche braucht oft nur Kühlschrank und Vorratsschrank.",
      scanHint: "Zu dunkel? Strichcode eintippen oder über die Suche finden.",
    }
  : {
      one: "Wein",
      many: "Weine",
      aOne: "einen Wein",
      search: "Wein suchen",
      unit: "Flasche",
      units: "Flaschen",
      unitShort: "Fl.",
      lagerExample: "Weinkeller oder Bar",
      firstLocation: "Regal",
      lagers: "Weinkeller, Bar, Regale",
      device: "Ein Gerät an der Bar braucht oft nur die Bar.",
      scanHint: "Zu dunkel? Strichcode eintippen oder über die Suche finden, auch mit der Kartennummer.",
    };
